import { Component, OnInit } from "@angular/core";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { ToastrService } from "ngx-toastr";
import { MatDialog } from "@angular/material/dialog";
import { AccessRequestResponseDialogComponent } from "./access-request-response-dialog/access-request-response-dialog.component";
import { Router } from "@angular/router";
import { EventEmitterService } from "src/app/Services/event-emitter.service";

export interface FlowAccessRequest {
  flowId: string;
  flowOwnerEmail: string;
  requesterEmail: string;
  contentTitle: string;
  contentType: string;
  requesterName: string;
  requesterUserName: string;
  requestMessage: string;
  status: string;
  requestedDateTime: string;
  respondedDateTime?: string;
  responseMessage?: string;
  flowName?: string;
  flowUrl?: string;
}

@Component({
  selector: "app-access-requests",
  templateUrl: "./access-requests.component.html",
  styleUrls: ["./access-requests.component.scss"],
})
export class AccessRequestsComponent implements OnInit {
  userData: any;
  accessRequests: FlowAccessRequest[] = [];
  filteredRequests: FlowAccessRequest[] = [];
  isLoading: boolean = false;
  searchTerm: string = "";
  statusFilter: string = "All";
  selectedRequests: string[] = [];
  toogleMenuClass: boolean = false;
  viewMode: 'table' | 'card' = 'table';

  constructor(
    private flowServiceService: FlowServiceService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private router: Router,
    private eventEmitterService: EventEmitterService
  ) {}

  ngOnInit(): void {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser"));
    
    // On mobile, default to card view (better UX for narrow screens)
    if (window && window.innerWidth <= 768) {
      this.viewMode = 'card';
    }
    
    // Set the correct menu state and page name
    this.eventEmitterService.setDashboardMenuStyleProperty("access-requests,Access Requests");
    
    this.loadAccessRequests();
  }

  loadAccessRequests(): void {
    this.isLoading = true;
    console.log("Loading access requests...");
    this.flowServiceService
      .getAccessRequestsByOwner(this.userData.emailAddress)
      .subscribe(
        (requests: FlowAccessRequest[]) => {
          console.log("Access requests loaded:", requests);
          this.accessRequests = requests;
          this.filteredRequests = requests;
          this.isLoading = false;
        },
        (error) => {
          console.error("Error loading access requests:", error);
          console.error(
            "Error details:",
            error.status,
            error.statusText,
            error.url
          );
          this.toastr.error(
            "Failed to load access requests: " +
              (error.error?.message || error.message || "Unknown error")
          );
          this.isLoading = false;
        }
      );
  }

  searchRequests(): void {
    if (!this.searchTerm.trim()) {
      this.filteredRequests = this.accessRequests;
    } else {
      this.filteredRequests = this.accessRequests.filter(
        (request) =>
          request.flowName
            ?.toLowerCase()
            .includes(this.searchTerm.toLowerCase()) ||
          request.requesterName
            ?.toLowerCase()
            .includes(this.searchTerm.toLowerCase()) ||
          request.requesterEmail
            ?.toLowerCase()
            .includes(this.searchTerm.toLowerCase())
      );
    }
    this.applyStatusFilter();
  }

  applyStatusFilter(): void {
    if (this.statusFilter === "All") {
      // Keep current filtered results
    } else {
      this.filteredRequests = this.filteredRequests.filter(
        (request) => request.status === this.statusFilter
      );
    }
  }

  onStatusFilterChange(): void {
    this.filteredRequests = this.accessRequests;
    this.searchRequests();
  }

  approveRequest(request: FlowAccessRequest): void {
    console.log("Opening approve dialog for request:", request);

    try {
      const dialogRef = this.dialog.open(AccessRequestResponseDialogComponent, {
        panelClass: "dialoguebox",
        data: {
          request: request,
          action: "approve",
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.flowServiceService
            .approveAccessRequest(
              request.flowId,
              request.requesterEmail,
              result.message
            )
            .subscribe(
              (response) => {
                this.toastr.success("Access request approved successfully");
                this.loadAccessRequests();
              },
              (error) => {
                console.error("Error approving request:", error);
                this.toastr.error("Failed to approve access request");
              }
            );
        }
      });
    } catch (error) {
      console.error("Error opening dialog:", error);
      this.toastr.error("Failed to open dialog");
    }
  }

  rejectRequest(request: FlowAccessRequest): void {
    console.log("Opening reject dialog for request:", request);

    try {
      const dialogRef = this.dialog.open(AccessRequestResponseDialogComponent, {
        panelClass: "dialoguebox",
        data: {
          request: request,
          action: "reject",
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.flowServiceService
            .rejectAccessRequest(
              request.flowId,
              request.requesterEmail,
              result.message
            )
            .subscribe(
              (response) => {
                this.toastr.success("Access request rejected successfully");
                this.loadAccessRequests();
              },
              (error) => {
                console.error("Error rejecting request:", error);
                this.toastr.error("Failed to reject access request");
              }
            );
        }
      });
    } catch (error) {
      console.error("Error opening dialog:", error);
      this.toastr.error("Failed to open dialog");
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case "pending":
        return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      default:
        return "status-pending";
    }
  }

  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case "pending":
        return "schedule";
      case "approved":
        return "check_circle";
      case "rejected":
        return "cancel";
      default:
        return "schedule";
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  }

  onRequestSelectionChange(requestId: string, isSelected: boolean): void {
    if (isSelected) {
      this.selectedRequests.push(requestId);
    } else {
      this.selectedRequests = this.selectedRequests.filter(
        (id) => id !== requestId
      );
    }
  }

  isRequestSelected(requestId: string): boolean {
    return this.selectedRequests.includes(requestId);
  }

  bulkApprove(): void {
    if (this.selectedRequests.length === 0) {
      this.toastr.warning("Please select requests to approve");
      return;
    }

    if (
      confirm(
        `Are you sure you want to approve ${this.selectedRequests.length} access requests?`
      )
    ) {
      const promises = this.selectedRequests.map((requestId) => {
        const request = this.accessRequests.find(
          (r) => `${r.flowId}-${r.requesterEmail}` === requestId
        );
        if (request) {
          return this.flowServiceService
            .approveAccessRequest(request.flowId, request.requesterEmail)
            .toPromise();
        }
        return Promise.resolve();
      });

      Promise.all(promises)
        .then(() => {
          this.toastr.success(
            `${this.selectedRequests.length} access requests approved successfully`
          );
          this.selectedRequests = [];
          this.loadAccessRequests();
        })
        .catch((error) => {
          console.error("Error bulk approving requests:", error);
          this.toastr.error("Failed to approve some access requests");
        });
    }
  }

  bulkReject(): void {
    if (this.selectedRequests.length === 0) {
      this.toastr.warning("Please select requests to reject");
      return;
    }

    if (
      confirm(
        `Are you sure you want to reject ${this.selectedRequests.length} access requests?`
      )
    ) {
      const promises = this.selectedRequests.map((requestId) => {
        const request = this.accessRequests.find(
          (r) => `${r.flowId}-${r.requesterEmail}` === requestId
        );
        if (request) {
          return this.flowServiceService
            .rejectAccessRequest(request.flowId, request.requesterEmail)
            .toPromise();
        }
        return Promise.resolve();
      });

      Promise.all(promises)
        .then(() => {
          this.toastr.success(
            `${this.selectedRequests.length} access requests rejected successfully`
          );
          this.selectedRequests = [];
          this.loadAccessRequests();
        })
        .catch((error) => {
          console.error("Error bulk rejecting requests:", error);
          this.toastr.error("Failed to reject some access requests");
        });
    }
  }

  selectAllRequests(): void {
    this.selectedRequests = this.filteredRequests.map(
      (request) => `${request.flowId}-${request.requesterEmail}`
    );
  }

  deselectAllRequests(): void {
    this.selectedRequests = [];
  }

  // Summary card methods
  getTotalRequests(): number {
    return this.accessRequests.length;
  }

  getPendingRequests(): number {
    return this.accessRequests.filter((request) => request.status === "Pending")
      .length;
  }

  getApprovedRequests(): number {
    return this.accessRequests.filter(
      (request) => request.status === "Approved"
    ).length;
  }

  getRejectedRequests(): number {
    return this.accessRequests.filter(
      (request) => request.status === "Rejected"
    ).length;
  }

  navigateToContent(request: any) {
    if (!request) return;
    const title = request.contentName || request.contentTitle || "content";
    const id = request.flowId;
    if (!id) {
      this.toastr.error("Unable to open content: missing id");
      return;
    }

    if (request.contentType == "Flow") {
      this.router.navigate(["/guide", this.generateUrlSlug(title), id]);
    } else if (request.contentType == "Bundle") {
      this.router.navigate(["/view-guide-pack/", id]);
    }
  }

  generateUrlSlug(html) {
    if (!html) return "";
    return html
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove accents (é → e, ñ → n)
      .replace(/[^a-zA-Z0-9\s-]/g, "") // Remove special characters except spaces and "-"
      .trim() // Trim spaces from start/end
      .replace(/\s+/g, "-") // Replace spaces with "-"
      .replace(/-+/g, "-") // Remove duplicate dashes
      .toLowerCase(); // Convert to lowercase
  }
  
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'table' ? 'card' : 'table';
  }
}
